import { todayParis, reportingPeriod } from "../utils/calendar.mjs";
import {
  userDailyState,
  periodIndicators,
  defaultDailyGoals,
} from "./daily.mjs";
import { shiftDate } from "../dashboard-metrics.mjs";
import { workHoursReport } from "./work-hours.mjs";
import { unpaidInvoiceTasks } from "./unpaid-invoices.mjs";
export const weeklyLabels = {
  callResults: "Appels saisis",
  decisionMakersReached: "Décideurs joints",
  needsDetected: "Besoins détectés",
  quotesSent: "Devis envoyés",
};
export function managementUsers(state) {
  const users = new Map((state.users || []).map((u) => [u.id, u.name || u.id]));
  state.clients.forEach((c) => users.set(c.ownerId, c.owner));
  Object.keys(state.goalsByUser || {}).forEach((id) => {
    if (!users.has(id)) users.set(id, id);
  });
  (state.workSchedules || []).forEach((s) => {
    if (!users.has(s.userId)) users.set(s.userId, s.userId);
  });
  (state.weeklyGoalPlans || []).forEach((s) => {
    if (!users.has(s.userId)) users.set(s.userId, s.userId);
  });
  return [...users.entries()];
}
export function setWeeklyGoalPlan(
  state,
  userId,
  week,
  goals,
  carry,
  actor,
  now = new Date(),
) {
  if (actor?.role !== "hyperadmin")
    throw new Error("Seul un hyperadmin peut fixer les objectifs.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(week || "") || Number.isNaN(Date.parse(week))) throw new Error("Semaine invalide.");
  const from = reportingPeriod("week", week).from;
  if (from < reportingPeriod("week", todayParis(now)).from)
    throw new Error("Une semaine passée ne peut pas être modifiée.");
  if (
    Object.keys(defaultDailyGoals).some(
      (k) => !Number.isInteger(goals[k]) || goals[k] < 0 || goals[k] > 10000,
    )
  )
    throw new Error("Objectifs : nombres entiers de 0 à 10 000.");
  if (!managementUsers(state).some(([id]) => id === userId))
    throw new Error("Utilisateur introuvable.");
  const plan = {
    userId,
    from,
    goals,
    carry: !!carry,
    updatedAt: now.toISOString(),
    updatedBy: actor.id,
  };
  return {
    ...state,
    weeklyGoalPlans: [
      ...(state.weeklyGoalPlans || []).filter(
        (p) => p.userId !== userId || p.from !== from,
      ),
      plan,
    ],
    weeklyGoalAudit: [...(state.weeklyGoalAudit || []), plan],
  };
}
export function weeklyGoalReport(state, userId, date, now = new Date()) {
  const week = reportingPeriod("week", date),
    current = reportingPeriod("week", todayParis(now)).from;
  const plans = (state.weeklyGoalPlans || [])
    .filter((p) => p.userId === userId && p.from <= week.from)
    .sort((a, b) => a.from.localeCompare(b.from));
  if (!plans.length) return null;
  const own = userDailyState(state, userId);
  const carry = Object.fromEntries(
    Object.keys(weeklyLabels).map((k) => [k, 0]),
  );
  let plan = plans[0],
    result;
  for (let from = plans[0].from; from <= week.from; from = shiftDate(from, 7)) {
    plan = plans.filter((p) => p.from <= from).at(-1);
    const actual =
      from > current
        ? Object.fromEntries(Object.keys(weeklyLabels).map((k) => [k, 0]))
        : periodIndicators(own, from, shiftDate(from, 6));
    const factors = Object.keys(weeklyLabels).map((key) => {
      const incoming = plan.carry ? carry[key] : 0;
      const target = plan.goals[key] + incoming;
      return {
        key,
        base: plan.goals[key],
        carry: incoming,
        target,
        actual: actual[key],
        remaining: Math.max(0, target - actual[key]),
      };
    });
    result = { from, to: shiftDate(from, 6), plan, factors };
    // Only elapsed weeks carry forward; the current week remains open.
    factors.forEach((f) => {
      carry[f.key] = from < current && plan.carry ? f.remaining : 0;
    });
  }
  return result;
}
export function managementAlerts(state, owners, now = new Date()) {
  const date = todayParis(now),
    alerts = [];
  for (const [userId, name] of owners) {
    const own = userDailyState(state, userId);
    const add = (kind, severity, title, items) => {
      if (items.length)
        alerts.push({
          id: `${userId}:${kind}`,
          userId,
          name,
          kind,
          severity,
          title,
          items,
        });
    };
    add(
      "engagement",
      "high",
      "Engagements non tenus",
      own.engagements.filter((e) => !e.done && e.due < date),
    );
    add(
      "tasks",
      "high",
      "Actions en retard",
      own.tasks.filter(
        (t) =>
          !t.done && t.category !== "manager" && t.due_date_original < date,
      ),
    );
    add(
      "quotes",
      "medium",
      "Devis envoyés sans relance prévue",
      own.quotes.filter(
        (q) =>
          q.status === "envoyé" &&
          !own.tasks.some((t) => !t.done && t.quoteId === q.id),
      ),
    );
    add("invoice", "high", "Factures impayées", unpaidInvoiceTasks(own, date));
    add(
      "risk",
      "medium",
      "Clients à risque",
      own.clients.filter(
        (c) =>
          c.riskReason ||
          [
            "À RÉACTIVER",
            "CLIENT DORMANT",
            "À RECONQUÉRIR",
            "CLIENT PERDU",
          ].includes(c.status),
      ),
    );
    const hours = workHoursReport(
      state,
      userId,
      reportingPeriod("week", date).from,
      date,
      now,
    );
    add(
      "hours",
      "medium",
      "Dépassements horaires à vérifier",
      hours.days.filter((d) => d.extra > 0),
    );
    const previous = weeklyGoalReport(
      state,
      userId,
      shiftDate(reportingPeriod("week", date).from, -7),
      now,
    );
    add(
      "goals",
      "medium",
      "Objectifs non atteints la semaine passée",
      previous?.factors.filter((f) => f.remaining > 0) || [],
    );
  }
  return alerts.sort(
    (a, b) => Number(b.severity === "high") - Number(a.severity === "high"),
  );
}

export function managementScope(state, agency = '') {
  if (!agency) return state;
  const clients = state.clients.filter(c => c.agency === agency);
  const ids = new Set(clients.map(c => c.id));
  const scoped = { ...state, clients };
  for (const key of ['tasks','results','events','needs','quotes','engagements','signals']) scoped[key] = (state[key] || []).filter(item => ids.has(item.clientId));
  return scoped;
}
